import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { convert } from 'html-to-text';
import { reqCreateBanner, reqDeleteBanner, reqGetBanners, reqUpdateBanner } from 'src/api/banner';
import { useBannerStore } from 'src/stores/useBannerStore';
import { sanitizeHtml } from 'src/utils/sanitizeHtml';
import type {
  Banner,
  BannerDescription,
  BannerDisplayStatus,
  BannerLocale,
  CreateBannerPayload,
  VisibleBanner
} from 'types/app/banner';
import { displayErrorMessage, displaySuccessMessage } from 'src/composables/useNotify';

function parseBannerDate(value: string): Date | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function normalizeBanner(banner: Banner): Banner {
  return {
    ...banner,
    id: String(banner.id)
  };
}

export function normalizeBanners(banners: unknown): Banner[] {
  if (!Array.isArray(banners)) {
    return [];
  }

  return banners.map((banner) => normalizeBanner(banner as Banner));
}

export function getBannerStatus(banner: Banner, now = new Date()): BannerDisplayStatus {
  const startDate = parseBannerDate(banner.startingDate);
  const expirationDate = parseBannerDate(banner.expirationDate);

  if (!startDate || !expirationDate) {
    return 'expired';
  }

  if (expirationDate < now) {
    return 'expired';
  }

  if (startDate > now) {
    return 'scheduled';
  }

  return 'active';
}

export function getDescriptionForLocale(banner: Banner, locale: string): string {
  const normalizedLocale = (locale.startsWith('fr') ? 'fr' : 'en') as BannerLocale;
  return banner.description[normalizedLocale] || banner.description.en || '';
}

export function getDescriptionExcerpt(banner: Banner, locale: string): string {
  const description = getDescriptionForLocale(banner, locale);
  return convert(sanitizeHtml(description), { wordwrap: false }).trim();
}

export const BANNER_PATH_ALL = 'ALL';

export function isGlobalBannerPath(bannerPath: string | null): bannerPath is null | '' | typeof BANNER_PATH_ALL {
  return bannerPath == null || bannerPath === '' || bannerPath === BANNER_PATH_ALL;
}

export function toBannerFormPath(bannerPath: string | null): string {
  return isGlobalBannerPath(bannerPath) ? BANNER_PATH_ALL : bannerPath;
}

export function matchesBannerPath(currentPath: string, bannerPath: string | null): boolean {
  if (isGlobalBannerPath(bannerPath)) {
    return true;
  }

  const normalizedCurrent = currentPath || '/';

  if (bannerPath === '/') {
    return normalizedCurrent === '/';
  }

  return normalizedCurrent === bannerPath || normalizedCurrent.startsWith(`${bannerPath}/`);
}

export function isBannerVisible(banner: Banner, currentPath: string, now = new Date()): boolean {
  return getBannerStatus(banner, now) === 'active' && matchesBannerPath(currentPath, banner.path);
}

function isNotExpired(banner: Banner, now = new Date()): boolean {
  const expirationDate = parseBannerDate(banner.expirationDate);
  if (!expirationDate) {
    return false;
  }

  return expirationDate >= now;
}

function sanitizeDescription(description: BannerDescription): BannerDescription {
  return {
    fr: sanitizeHtml(description.fr),
    en: sanitizeHtml(description.en)
  };
}

export function useBanners() {
  const route = useRoute();
  const { locale, t } = useI18n();
  const bannerStore = useBannerStore();
  const { liveBanners, dismissedBannerIds, isLoadingLiveBanners, hasLiveBannersError } = storeToRefs(bannerStore);

  const banners = ref<Banner[]>([]);
  const isLoading = ref(false);
  const isSubmitting = ref(false);
  const hasError = ref(false);

  const activeBanners = computed(() =>
    banners.value
      .filter((banner) => isNotExpired(banner))
      .map((banner) => ({
        ...banner,
        status: getBannerStatus(banner)
      }))
  );

  const expiredBanners = computed(() =>
    banners.value
      .filter((banner) => !isNotExpired(banner))
      .map((banner) => ({
        ...banner,
        status: 'expired' as BannerDisplayStatus
      }))
  );

  async function fetchBanners() {
    isLoading.value = true;
    hasError.value = false;

    try {
      const response = await reqGetBanners();
      banners.value = normalizeBanners(response);
    } catch (error) {
      hasError.value = true;
      banners.value = [];
      console.error('Failed to load banners', error);
      displayErrorMessage(t('administration.banners.load_error'));
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchActiveBanners() {
    await bannerStore.fetchActiveBanners();
  }

  const visibleBanners = computed<VisibleBanner[]>(() =>
    liveBanners.value
      .filter((banner) => isBannerVisible(banner, route.path) && !dismissedBannerIds.value.includes(banner.id))
      .map((banner) => ({
        id: banner.id,
        type: banner.type,
        description: getDescriptionForLocale(banner, locale.value)
      }))
  );

  function dismissBanner(id: string) {
    bannerStore.dismissBanner(id);
  }

  async function refreshBannersAfterMutation() {
    try {
      await Promise.all([fetchBanners(), fetchActiveBanners()]);
    } catch (error) {
      console.error('Failed to refresh banners after mutation', error);
    }
  }

  async function createBanner(payload: CreateBannerPayload) {
    isSubmitting.value = true;

    try {
      const sanitizedPayload = {
        ...payload,
        description: sanitizeDescription(payload.description)
      };
      await reqCreateBanner(sanitizedPayload);
    } catch (error) {
      console.error('Failed to create banner', error);
      displayErrorMessage(t('administration.banners.create_error'));
      return false;
    } finally {
      isSubmitting.value = false;
    }

    displaySuccessMessage(t('administration.banners.create_success'));
    await refreshBannersAfterMutation();
    return true;
  }

  async function updateBanner(id: string, payload: CreateBannerPayload) {
    isSubmitting.value = true;

    try {
      const sanitizedPayload = {
        ...payload,
        description: sanitizeDescription(payload.description)
      };
      await reqUpdateBanner(id, sanitizedPayload);
    } catch (error) {
      console.error('Failed to update banner', error);
      displayErrorMessage(t('administration.banners.update_error'));
      return false;
    } finally {
      isSubmitting.value = false;
    }

    displaySuccessMessage(t('administration.banners.update_success'));
    await refreshBannersAfterMutation();
    return true;
  }

  async function deleteBanner(id: string) {
    isSubmitting.value = true;

    try {
      await reqDeleteBanner(id);
      banners.value = banners.value.filter((banner) => banner.id !== id);
    } catch (error) {
      console.error('Failed to delete banner', error);
      displayErrorMessage(t('administration.banners.delete_error'));
      return false;
    } finally {
      isSubmitting.value = false;
    }

    displaySuccessMessage(t('administration.banners.delete_success'));
    await refreshBannersAfterMutation();
    return true;
  }

  return {
    banners,
    activeBanners,
    expiredBanners,
    visibleBanners,
    isLoading,
    isLoadingLiveBanners,
    isSubmitting,
    hasError,
    hasLiveBannersError,
    locale,
    fetchBanners,
    fetchActiveBanners,
    createBanner,
    updateBanner,
    deleteBanner,
    dismissBanner,
    getDescriptionForLocale,
    getDescriptionExcerpt
  };
}
